import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Scales, CircleNotch, ShieldCheck } from '@phosphor-icons/react';
import { confirmPayment, getSessionStatus, type SessionStatus } from '@/lib/onboardingApi';

/**
 * Confirm and create — the last step of the `/onboarding` signup journey.
 *
 * ── THIS PAGE USED TO ASK FOR A CARD, AND SAID THINGS THAT WERE NOT TRUE ─────────────────────
 * It rendered a cardholder-name, full card-number, expiry and CVV field, and told the visitor
 * "Secure payment", "Payments are processed securely", "Your workspace will be ready in seconds
 * after payment", "30-day free trial included" and "You will not be charged until your trial
 * ends. Cancel any time."
 *
 * None of it was so. There is no payment processor in this project
 * (`lib/finance/payment-provider.ts` is an unimplemented interface); the endpoint behind the
 * form charged nothing and discarded every value except the last four digits;
 * `CAPABILITY-REGISTER.md` lists SaaS Billing as **Future**; and whether a card should be asked
 * for at all is still an open commercial question in `docs/BILLING-DECISIONS-NEEDED.html`.
 * Meanwhile `runProvisioning` creates the workspace with `status: 'active'` and never sets
 * `trialEndsAt`, so there is no trial clock, no read-only lock and nothing to be charged for.
 *
 * Collecting a PAN and a CVV in our own DOM is not harmless just because the values are thrown
 * away: it puts this origin in scope for card handling, and every extension, autofill store,
 * crash reporter and session recorder on the page sees those fields.
 *
 * The step that remains does the part that was always real — the person confirms what is about
 * to be created, and the session is released to provisioning. It is the same treatment given to
 * the marketing-page signup modal, so the product's two signup journeys now say the same thing.
 */
export default function OnboardingPaymentPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [session, setSession] = useState<SessionStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // An abandoned signup expires after 24h → don't strand the user on a dead form; offer a restart.
  const [sessionDead, setSessionDead] = useState(false);

  /** A dead token looks the same whichever call discovers it. */
  function handleDeadOr(err: unknown, fallback: string) {
    const resp = (err as { response?: { status?: number; data?: { error?: { message?: string } } } })?.response;
    const msg = resp?.data?.error?.message;
    if (resp?.status === 404 || resp?.status === 410 || /expired|not found|onboarding session/i.test(msg ?? '')) {
      setSessionDead(true);
      return;
    }
    setError(msg ?? fallback);
  }

  // Show the person what they are about to create. The summary is read from the session they
  // already started, so this page states facts rather than asking for anything.
  useEffect(() => {
    if (!token) return;
    let alive = true;
    getSessionStatus(token)
      .then((s) => { if (alive) setSession(s); })
      .catch((err) => { if (alive) handleDeadOr(err, 'Could not load your signup.'); });
    return () => { alive = false; };
  }, [token]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token || loading) return;
    setError('');
    setLoading(true);
    try {
      await confirmPayment(token);
      navigate(`/onboarding/provisioning/${token}`);
    } catch (err: unknown) {
      handleDeadOr(err, 'Your workspace could not be created. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  const header = (
    <header className="border-b border-[#e5e3de] bg-white px-8 py-4">
      <div className="mx-auto max-w-6xl">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded bg-[#1a2f4e]">
            <Scales className="h-4 w-4 text-white" weight="duotone" />
          </div>
          <span className="text-sm font-semibold tracking-tight text-[#1a1915]">Jurivoxa</span>
        </Link>
      </div>
    </header>
  );

  if (sessionDead) {
    return (
      <div className="min-h-screen bg-[#f8f7f4]">
        {header}
        <main className="mx-auto max-w-md px-6 py-20 text-center">
          <h1 className="mb-2 text-xl font-semibold text-[#1a1915]">Your signup session expired</h1>
          <p className="mb-8 text-sm text-[#6b6961]">
            For security, an unfinished signup is only held for 24 hours. No payment was taken and
            nothing was created. Start again — it only takes a minute.
          </p>
          <button
            type="button"
            onClick={() => navigate('/onboarding')}
            className="inline-flex items-center justify-center gap-2 rounded bg-[#1a2f4e] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#243d61]"
          >
            Start again
          </button>
        </main>
      </div>
    );
  }

  const workspaceName =
    session?.organisationName?.trim() ||
    [session?.firstName, session?.lastName].filter(Boolean).join(' ').trim() ||
    '—';

  const summary = [
    { k: 'Workspace', v: workspaceName },
    { k: 'Type', v: session?.workspaceType === 'chambers' ? 'Chambers' : 'Single barrister' },
    { k: 'Plan', v: session ? session.plan.charAt(0).toUpperCase() + session.plan.slice(1) : '—' },
    { k: 'Email', v: session?.email ?? '—' },
  ];

  return (
    <div className="min-h-screen bg-[#f8f7f4]">
      {header}

      <main className="mx-auto max-w-md px-6 py-12">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-[#1a1915]">Confirm and create</h1>
          <p className="mt-1 text-sm text-[#6b6961]">
            Your workspace will be ready in seconds.
          </p>
        </div>

        {/*
          What this says is limited to what the system does: it takes nothing and creates the
          workspace with full access. The banner it replaced promised a 30-day trial this path
          never creates and a charge nothing in the project can make.
        */}
        <div className="mb-6 flex items-start gap-2.5 rounded-lg border border-[#1a2f4e]/20 bg-[#1a2f4e]/5 px-4 py-3">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#1a2f4e]" weight="duotone" />
          <div>
            <p className="text-sm font-medium text-[#1a2f4e]">No card required</p>
            <p className="text-xs text-[#4a5568]">
              We take no payment during signup and collect no card details. Your workspace is
              created with full access.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 rounded-lg border border-[#e5e3de] bg-white p-8">
          <div className="divide-y divide-[#e5e3de] rounded border border-[#e5e3de]">
            {summary.map((row) => (
              <div key={row.k} className="flex items-baseline justify-between gap-3 px-3 py-2.5">
                <span className="shrink-0 text-xs text-[#6b6961]">{row.k}</span>
                <span className="break-words text-right text-xs text-[#1a1915]">{row.v}</span>
              </div>
            ))}
          </div>

          {error && (
            <p role="alert" className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading || !session}
            className="flex w-full items-center justify-center gap-2 rounded bg-[#1a2f4e] py-2.5 text-sm font-medium text-white hover:bg-[#243d61] disabled:opacity-60"
          >
            {loading
              ? <><CircleNotch className="h-4 w-4 animate-spin" /> Creating…</>
              : 'Create my workspace'}
          </button>

          <p className="text-center text-xs text-[#9c9990]">
            We&rsquo;ll email {session?.email ?? 'you'} a link to set your password.
          </p>
        </form>
      </main>
    </div>
  );
}
